import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo } from "react";
import ReactDatePicker from "react-datepicker";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { generateDefaultValues, schema, Schema } from "./schema";
import {
  OnboardingStatus,
  ProjectsLocationsData,
  UpdateProjectLocationPayload,
} from "../../../../../../models/ProjectData";
import { HookForm } from "../../../../../HookForm/HookForm";

interface IOnboardingStatusFormProps {
  projectLocation: ProjectsLocationsData;
  handleClose: () => void;
  handleSubmit: (payload: UpdateProjectLocationPayload) => void;
}

export const OnboardingStatusForm = (props: IOnboardingStatusFormProps) => {
  const { projectLocation, handleClose, handleSubmit } = props;
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `projectManagementPage.projectFacilityDetailsModal.onboardingStatus.${key}`
    );

  const defaultValues = useMemo(() => {
    return generateDefaultValues(projectLocation);
  }, [projectLocation]);

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const selectedStatus = form.watch("onboardingStatus");

  const onSubmit = async (data: Schema) => {
    const payload: UpdateProjectLocationPayload = {
      onboarding_status: data.onboardingStatus,
      onboarding_scheduled_date:
        data.onboardingStatus === "SCHEDULED"
          ? data.onboardingScheduledDate
          : undefined,
      onboarding_completed_date:
        data.onboardingStatus === "COMPLETE"
          ? data.onboardingCompletedDate
          : undefined,
    };
    handleSubmit(payload);
  };

  const getStatusColor = (status: OnboardingStatus) => {
    switch (status) {
      case "INCOMPLETE":
        return "#F44336";
      case "SCHEDULED":
        return "#FF9800";
      case "COMPLETE":
        return "#4CAF50";
      default:
        return "#9E9E9E";
    }
  };

  return (
    <div className="modal modal-default modal-onboarding-status-form">
      <div className="modal-mains">
        <button className="btn-close" onClick={handleClose}></button>
        <div className="modal-mains__body">
          <div className="top-title">
            <h3>{t("edit_onboarding_status")}</h3>
          </div>
          <HookForm
            onSubmit={onSubmit}
            form={form}
            submitButtonLabel={t("update")}
            onCancel={handleClose}
            showCancelButton
          >
            <div className="form-row">
              <Controller
                control={form.control}
                name="onboardingStatus"
                render={({ field }) => (
                  <div className="status-select-container">
                    <label
                      className="hook-form-input-label"
                      htmlFor={field.name}
                    >
                      {t("onboarding_status")}
                    </label>
                    <select
                      {...field}
                      className="status-select"
                      style={{
                        borderColor: field.value
                          ? getStatusColor(field.value as OnboardingStatus)
                          : "#ccc",
                      }}
                    >
                      <option value="INCOMPLETE">
                        {t("status_incomplete")}
                      </option>
                      <option value="SCHEDULED">{t("status_scheduled")}</option>
                      <option value="COMPLETE">{t("status_complete")}</option>
                    </select>
                  </div>
                )}
              />

              {selectedStatus === "SCHEDULED" && (
                <Controller
                  control={form.control}
                  name="onboardingScheduledDate"
                  render={({ field }) => (
                    <div className="date-input-container">
                      <label
                        className="hook-form-input-label"
                        htmlFor={field.name}
                      >
                        {t("scheduled_date")}
                      </label>
                      <ReactDatePicker
                        locale="ja"
                        dateFormat="yyyy/MM/dd"
                        selected={field.value}
                        onChange={field.onChange}
                        className="date-input"
                      />
                    </div>
                  )}
                />
              )}

              {selectedStatus === "COMPLETE" && (
                <Controller
                  control={form.control}
                  name="onboardingCompletedDate"
                  render={({ field }) => (
                    <div className="date-input-container">
                      <label
                        className="hook-form-input-label"
                        htmlFor={field.name}
                      >
                        {t("completed_date")}
                      </label>
                      <ReactDatePicker
                        locale="ja"
                        dateFormat="yyyy/MM/dd"
                        selected={field.value}
                        onChange={field.onChange}
                        className="date-input"
                      />
                    </div>
                  )}
                />
              )}
            </div>
          </HookForm>
        </div>
      </div>
    </div>
  );
};
